import { useEffect, useRef, useState } from 'react';
import { addDays, type Contact, type MessageOrigin, type SchemaOptions, type User } from '@crmsy/shared';
import { todayIso } from '../lib/today.js';
import { fetchDrafts } from '../lib/api.js';
import { db } from '../lib/db.js';
import { Modal } from './Modal.jsx';
import { NextActionSelect, NextFollowUp } from './Dialogs.jsx';
import { useT, type Key } from '../i18n/index.js';

const field = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent';

export interface SentMessageValue {
  text: string;
  origin: MessageOrigin;
  nextContactDate: string | null;
  nextAction: string | null | undefined;
}

/** Steer chips: the label is localized, the hint sent to the model stays English. */
const STEER: { key: Key; hint: string }[] = [
  { key: 'message.steer.shorter', hint: 'shorter' },
  { key: 'message.steer.warmer', hint: 'warmer' },
  { key: 'message.steer.direct', hint: 'more direct' },
  { key: 'message.steer.call', hint: 'ask for a call' },
  { key: 'message.steer.bg', hint: 'in Bulgarian' },
];

/**
 * Message (§9, D-23): five AI drafts fetched at once, cycled instantly; steer chips
 * and a hint fetch a fresh batch; "Request more" asks for five that differ from the
 * ones shown. The text is editable. "Sent" asks for the next follow-up like Followed up
 * and saves one `message` entry with its origin.
 */
export function MessageDialog({ contact, schema, user, ai, onSave, onClose }: { contact: Contact; schema: SchemaOptions; user: User; ai: boolean; onSave: (v: SentMessageValue) => void; onClose: () => void }) {
  const t = useT();
  const [drafts, setDrafts] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState<'batch' | 'more' | null>(ai ? 'batch' : null);
  const [error, setError] = useState<string | null>(null);
  const [steer, setSteer] = useState<string[]>([]);
  const [hint, setHint] = useState('');
  const [phase, setPhase] = useState<'compose' | 'sent'>('compose');
  const [days, setDays] = useState<number | 'custom' | 'none'>(7);
  const [custom, setCustom] = useState(addDays(todayIso(), 14));
  const [nextAction, setNextAction] = useState(contact.nextAction ?? '');
  const textRef = useRef<HTMLTextAreaElement>(null);

  const hintText = [...steer, hint.trim()].filter(Boolean).join(', ');

  const load = async (mode: 'batch' | 'more') => {
    setLoading(mode);
    setError(null);
    try {
      const r = await fetchDrafts({ contactId: contact.id, hint: hintText || undefined, exclude: mode === 'more' ? drafts : undefined });
      if (mode === 'more') {
        setDrafts((d) => [...d, ...r.drafts]);
        setIndex(drafts.length);
        setText(r.drafts[0] ?? '');
      } else {
        setDrafts(r.drafts);
        setIndex(0);
        setText(r.drafts[0] ?? '');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(null);
      textRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!ai) return;
    // D-28: drafts made ahead of time are in the replica; open instantly, else ask now.
    void db.drafts.get(contact.id).then((cached) => {
      if (cached && cached.drafts.length) {
        setDrafts(cached.drafts);
        setIndex(0);
        setText(cached.drafts[0]!);
        setLoading(null);
        textRef.current?.focus();
      } else void load('batch');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cycle = () => {
    if (!drafts.length) return;
    const next = index + 1;
    if (next < drafts.length) {
      setIndex(next);
      setText(drafts[next]!);
    } else void load('more');
  };

  const origin: MessageOrigin = !drafts.length ? 'manual' : text.trim() === drafts[index]?.trim() ? 'ai' : 'ai-edited';
  const today = todayIso();
  const date = days === 'none' ? null : days === 'custom' ? custom : addDays(today, days);
  const canSend = text.trim().length > 0 && !loading;

  const save = () => {
    if (!canSend) return;
    if (days === 'custom' && !/^\d{4}-\d{2}-\d{2}$/.test(custom)) return;
    onSave({ text: text.trim(), origin, nextContactDate: date, nextAction: nextAction === (contact.nextAction ?? '') ? undefined : nextAction || null });
    onClose();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (phase === 'compose') setPhase('sent');
      else save();
    }
  };

  return (
    <Modal
      title={t('message.title', { name: contact.companyName || contact.contact })}
      onClose={onClose}
      width="md"
      hint={<><kbd>Ctrl+Enter</kbd> {phase === 'compose' ? t('dialog.sent') : t('dialog.hintSave')} · <kbd>Esc</kbd> {t('dialog.hintCancel')}</>}
    >
      {ai && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <button type="button" className="btn" onClick={cycle} disabled={!!loading || !drafts.length} title={t('message.nextDraft')}>
            {t('message.cycle')} {drafts.length ? <span className="text-muted">{index + 1}/{drafts.length}</span> : null}
          </button>
          {index >= drafts.length - 1 && drafts.length > 0 && (
            <button type="button" className="btn" onClick={() => void load('more')} disabled={!!loading}>
              {loading === 'more' ? t('message.asking') : t('message.more')}
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className="btn" onClick={() => void navigator.clipboard?.writeText(text)} disabled={!text.trim()}>
            {t('message.copy')}
          </button>
        </div>
      )}
      <textarea
        ref={textRef}
        data-autofocus
        rows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={loading === 'batch' ? t('message.draftingFive') : ai ? t('message.placeholderAi') : t('message.placeholderManual')}
        className={field}
        disabled={loading === 'batch'}
      />
      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted">
        <span>{loading === 'batch' ? t('message.geminiDrafting') : error ? <span className="text-danger">{error}</span> : t('message.origin', { origin: t(`origin.${origin}`) })}</span>
        <span className="flex-1" />
        <span>{t('message.toFrom', { to: contact.contact || contact.companyName, from: user.name })}</span>
      </div>

      {ai && phase === 'compose' && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {STEER.map((s) => (
            <button key={s.key} type="button" className="chip" data-on={steer.includes(s.hint)} onClick={() => setSteer((v) => (v.includes(s.hint) ? v.filter((x) => x !== s.hint) : [...v, s.hint]))}>
              {t(s.key)}
            </button>
          ))}
          <input
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void load('batch');
              }
            }}
            placeholder={t('message.steerPlaceholder')}
            className="min-w-40 flex-1 rounded-full border border-line bg-paper px-3 py-1 text-xs outline-none focus:border-accent"
          />
          <button type="button" className="btn" onClick={() => void load('batch')} disabled={!!loading}>
            {loading === 'batch' ? t('message.drafting') : t('message.redraft')}
          </button>
        </div>
      )}

      {phase === 'sent' && (
        <div className="mt-4 rounded-lg border border-line bg-surface p-3">
          <div className="text-xs font-medium text-muted">{t('message.sentNext')}</div>
          <NextFollowUp days={days} setDays={setDays} custom={custom} setCustom={setCustom} date={date} />
          <div className="mt-2 text-xs font-medium text-muted">{t('followedUp.nextAction')}</div>
          <NextActionSelect contact={contact} schema={schema} value={nextAction} onChange={setNextAction} onKeyDown={onKeyDown} />
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        {phase === 'compose' ? (
          <button type="button" className="btn btn-primary" onClick={() => setPhase('sent')} disabled={!canSend}>
            {t('message.sent')}
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={() => setPhase('compose')}>{t('dialog.back')}</button>
            <button type="button" className="btn btn-primary" onClick={save}>{t('dialog.save')}</button>
          </>
        )}
      </div>
    </Modal>
  );
}
